import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
import { UncontrolledDropdown, Form, DropdownMenu, DropdownToggle, DropdownItem } from 'reactstrap'
import {
  IconEdit,
  IconCheck,
  IconExternalLink,
  IconHelpCircle,
  IconWindowMaximize,
  IconWindowMinimize,
  IconX,
  IconChevronLeft
} from '@tabler/icons-react'
import { useDispatch, useSelector } from 'react-redux'
import { centraliGuides, staticAssetsPath } from '../../../configs/constants'
import { useSasToken } from '../../../_hooks/useSasToken'
import { useThemeManager } from '../../../_hooks/useThemeManager'
import _endpoints from '../../../_endpoints'
import {
  setAliOpenSideBar,
  setSelectedAgent,
  setFullScreen
} from '../../../_redux/actions/chatbotActions'
import { NewChat, Collapse } from './icons/Icons'
//@ts-ignore
import { Button, Input, Avatar } from 'dsm-react-lib'
import { useForm } from 'react-hook-form'
import AliFeedbackModal from './modals/AliFeedbackModal'
import { useChatService } from './services/chatService'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { checkIsDrawerMode } from './utils/helpers'
import { ChatbotContext } from './context/ChatbotProvider'
import { appInsights } from '../../../_services/AppInsights'
import { EventType } from '../../../utility/types'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { Tooltip } from 'react-tooltip'
import { AgentData } from './utils/types'
import { useFeatureGate } from '../../../_hooks/useFeatureGate'
import { setIsChatOpen, setProducts } from '../../../_redux/actions/dataMarketPlaceActions'
import { getInitials } from '../../../utility/Utils'
import { getProjectIcon, getProjectColour } from './project/projectConstants'

interface ChatHeaderProps {
  showSetting: boolean
  setShowSetting: (showSetting: boolean) => void
  personaList?: any
  setPersonaList?: (personaList: any) => void
  onNewConversationStart: (persona: any) => void
  showHistory: boolean
  setShowHistory: (showHistory: boolean) => void
  setIsAliTourTriggered: (isAliTourTriggered: boolean) => void
  isRightPanelOpen: boolean
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  showSetting,
  setShowSetting,
  personaList,
  setPersonaList,
  showHistory,
  setShowHistory,
  onNewConversationStart,
  setIsAliTourTriggered,
  isRightPanelOpen = false
}) => {
  const { selectedAgent, isFullScreen } = useSelector((state: any) => state.chatbotReducer)
  const { currentChat, setCurrentChat, messages, setMessages, fetchingMsgs, setRawMessages } =
    useContext(ChatbotContext)
  const { projectThreadIdsRef, setSelectedProject } = useContext(ChatbotContext)
  const dispatch = useDispatch()
  const assistants = useSelector((state: any) => state.agentReducer.agentsList)
  const [isEditName, setIsEditName] = useState(false)
  const [isTitleHovered, setIsTitleHovered] = useState(false)
  const [title, setTitle] = useState('Chat name')
  const [tempTitle, setTempTitle] = useState('')
  const { errors, clearErrors, register, trigger, handleSubmit } = useForm()
  const { appendToken } = useSasToken()
  const { isDarkTheme } = useThemeManager()
  const CentraliIcon = appendToken(
    `${staticAssetsPath}/images/logo/${isDarkTheme ? 'Centrali-Icon-dark' : 'Centrali-Icon'}.svg`
  )
  const location = useLocation()

  const isMarketplace =
    location.pathname.includes('data-marketplace') ||
    location.pathname.includes('data-product-admin')
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { updateChatTitle, isUpdatingChatTitle } = useChatService()
  const navigate = useNavigate()
  const isDrawerMode = checkIsDrawerMode()

  // The project this conversation belongs to (set when opened from within a project),
  // used to show a clickable project chip in the header. Resolved from currentChat.id so
  // it clears automatically for standalone chats and new conversations.
  // eslint-disable-next-line react-hooks/refs
  const activeProject = currentChat?.id ? projectThreadIdsRef.current.get(currentChat.id) : null
  // Icon component is resolved dynamically from data, not declared at module scope, by design.
  const ActiveProjectIcon = activeProject ? getProjectIcon(activeProject.icon) : null

  const onOpenActiveProject = () => {
    if (!activeProject) return
    setSelectedProject(activeProject)
    setMessages([])
    setRawMessages([])
    // Clear the current chat so ProjectView's landing-view effect (keyed on currentChat.id)
    // detects the return to the project and shows the threads/files list again.
    setCurrentChat({ id: null, title: null })
    if (!isDrawerMode) navigate(`/chat/project/${activeProject.id}`)
  }

  //@ts-ignore
  const trackFeedbackClick = useTrackEvent(appInsights, EventType.AliFeedbackClicked)
  //@ts-ignore
  const trackChatHistoryOpened = useTrackEvent(appInsights, EventType.ChatHistoryClicked)
  //@ts-ignore
  const trackNewChat = useTrackEvent(appInsights, EventType.NewChatButtonClicked)
  const { value: centrali3_0 } = useFeatureGate('centrali3_0', false)

  const userRole = assistants?.find((agent: AgentData) => agent.id === selectedAgent?.id)?.metadata
    ?.userRole

  useEffect(() => {
    if (isEditName && inputRef.current) {
      const input = inputRef.current
      input.focus()
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }, [isEditName])

  //Show typing effect when title is generated for the first time
  useEffect(() => {
    let index = 0
    const typingInterval = setInterval(() => {
      if (currentChat?.title && currentChat?.recentlyUpdated) {
        const newTitle = currentChat.title.slice(0, index + 1) // Update to show the complete title progressively
        setTitle(newTitle)
        index += 1

        if (index === currentChat.title.length) {
          clearInterval(typingInterval)
        }
      } else {
        setTitle(currentChat?.title)
      }
    }, 50) // Adjust the speed (in milliseconds) here

    setTempTitle(currentChat?.title)
    return () => clearInterval(typingInterval)
  }, [currentChat])

  //for new conversation, automatically set personal assistant as selected
  useEffect(() => {
    if (
      centrali3_0 &&
      currentChat?.id === null &&
      (!selectedAgent || selectedAgent.id === 'd2d12f41-449c-4245-93f1-4242c9e4eb04') &&
      assistants &&
      assistants.length > 0 &&
      !location.state?.fromAgentCard
    ) {
      const personalAssistant = assistants.find((agent: AgentData) => agent.isPersonalAssistant)
      if (personalAssistant) {
        dispatch(setSelectedAgent({ ...personalAssistant }))
      }
    }
  }, [currentChat?.id, centrali3_0, selectedAgent, assistants, location.state, dispatch])

  const onTitleSave = async () => {
    const isValid = await trigger()
    if (isValid) {
      updateChatTitle(currentChat?.id, tempTitle)
      toast.success('Chat title updated successfully')
      setTitle(tempTitle)
      setCurrentChat({
        ...currentChat,
        title: tempTitle
      })

      setIsEditName(false)
    }
  }

  const assistantSection = (
    <div className="h-10 rounded-1 flex justify-center items-center">
      <Avatar
        size={'28px'}
        id="personaName"
        data-tooltip-id="persona"
        content={selectedAgent?.avatarUrl || getInitials(selectedAgent?.name)}
        className="shrink-0 me-50 !cursor-default"
      />
      {!isRightPanelOpen && !isDrawerMode && (
        <div
          id="personaName"
          className="inline-block whitespace-nowrap overflow-hidden text-ellipsis text-body-strong mr-2"
          title={selectedAgent?.name}>
          {selectedAgent?.name?.length > 30
            ? `${selectedAgent.name.slice(0, 30)}...`
            : selectedAgent?.name}
        </div>
      )}
    </div>
  )

  const onTitleChange = (name: string) => {
    setTempTitle(name)
    clearErrors('chatName')
  }

  return (
    <>
      <div
        className={`${'flex justify-between items-center'} ${
          isDrawerMode ? 'm-50' : 'm-1'
        } chat-primary`}>
        {(!isEditName || !isDrawerMode) && (
          <div className={`flex items-center ${!isDrawerMode ? 'justify-self-start' : ''} min-w-0`}>
            {!showHistory && (
              <>
                <div className="me-50 flex justify-end p-50 hover:bg-theme-hover rounded-full transition-colors duration-200 outline-none">
                  <Collapse
                    size={24}
                    className="outline-none"
                    data-tooltip-id="openSidebar"
                    data-testid="openSidebar"
                    onClick={() => {
                      if (!isDrawerMode) setShowHistory(!showHistory)
                      trackChatHistoryOpened({ chatHistory: 'Opened' })
                    }}
                    onMouseEnter={() => {
                      if (isDrawerMode) setShowHistory(true)
                    }}
                  />
                </div>
                <Tooltip
                  id="openSidebar"
                  place="bottom"
                  className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                  Open sidebar
                </Tooltip>
                <div
                  className="me-50 flex justify-end p-50 cursor-pointer hover:bg-theme-hover rounded-full transition-colors duration-200"
                  id="newChat">
                  <NewChat
                    data-tooltip-id="newChat"
                    data-testid="newChat"
                    className=""
                    size={24}
                    onClick={() => {
                      trackNewChat({ location: 'Topbar' })
                      onNewConversationStart(selectedAgent)
                      dispatch(setProducts([]))
                    }}
                  />
                </div>
                <Tooltip
                  id="newChat"
                  place="bottom"
                  className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                  New chat
                </Tooltip>
              </>
            )}
            <div
              className={`flex flex-shrink min-w-0 ${
                !isDrawerMode ? 'max-w-[180px]' : 'max-w-[150px]'
              }`}>
              {!location?.pathname?.includes('agent') ||
              location?.pathname?.includes('/workspace') ? (
                selectedAgent && assistantSection
              ) : location?.pathname?.includes('/manage-agent') ? (
                <div
                  className="flex cursor-pointer items-center ml-2 gap-50 text-body-default"
                  onClick={() => navigate('/chat')}>
                  <IconChevronLeft size={20} /> Back to chat
                </div>
              ) : (
                <div
                  className="flex cursor-pointer items-center ml-2 gap-50 text-body-default"
                  onClick={() => navigate('/manage-agents')}>
                  <IconChevronLeft size={20} /> My agents
                </div>
              )}
            </div>
          </div>
        )}

        {/* Central icons */}
        {location?.pathname?.startsWith('/chat') && (
          <div
            className={`flex justify-center items-center text-center gap-2 ${
              !isDrawerMode ? 'justify-self-stretch px-2' : 'flex-1'
            } min-w-0 h-10`}>
            {isEditName ? (
              <Form
                id="saveForm"
                onSubmit={handleSubmit(onTitleSave)}
                className={`w-full min-w-0 ${isRightPanelOpen ? 'max-w-[360px]' : 'max-w-md'}`}>
                <div className="flex min-w-0 items-center justify-center gap-1">
                  <Input
                    type="text"
                    id={`chatName`}
                    name="chatName"
                    className="min-w-0 flex-1 text-center"
                    placeholder="Chat name"
                    defaultValue={tempTitle}
                    invalid={'chatName' in errors}
                    ref={(e: any) => {
                      register(e, { required: true })
                      inputRef.current = e
                    }}
                    helperText={errors.chatName ? 'Chat name is mandatory' : ''}
                    onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                      await trigger('chatName')
                      onTitleChange(e.target.value)
                    }}
                    data-testid="chatName"
                  />
                  <Button
                    label="Cancel"
                    className="flex-shrink-0"
                    color="var(--color-primary)"
                    data-testid="cancel"
                    iconOnly={isDrawerMode || isRightPanelOpen}
                    {...(isDrawerMode || isRightPanelOpen ? { icon: <IconX size={16} /> } : {})}
                    onClick={() => {
                      setIsEditName(false)
                      clearErrors('chatName')
                    }}
                  />
                  <Button
                    color="var(--color-primary)"
                    className="flex-shrink-0"
                    onClick={onTitleSave}
                    disabled={isUpdatingChatTitle}
                    type="submit"
                    label={isUpdatingChatTitle ? 'Saving' : 'Save'}
                    data-testid="save"
                    iconOnly={isDrawerMode || isRightPanelOpen}
                    {...(isDrawerMode || isRightPanelOpen ? { icon: <IconCheck size={16} /> } : {})}
                  />
                </div>
              </Form>
            ) : (
              <div className="flex items-center justify-center min-w-0 gap-1 px-2 bg-theme-surface [--chat-header-mask:linear-gradient(to_right,transparent,black_1rem,black_calc(100%_-_1rem),transparent)] [mask-image:var(--chat-header-mask)] [-webkit-mask-image:var(--chat-header-mask)]">
                {activeProject && ActiveProjectIcon && (
                  <>
                    <button
                      type="button"
                      onClick={onOpenActiveProject}
                      className="inline-flex items-center justify-center w-10 h-10 rounded-full theme-hover-surface border-0 bg-transparent cursor-pointer outline-none flex-shrink-0"
                      data-testid="chat-project-chip"
                      data-tooltip-id="chatProjectChip">
                      {/* eslint-disable-next-line react-hooks/static-components */}
                      <ActiveProjectIcon
                        size={26}
                        style={{ color: getProjectColour(activeProject.color) }}
                      />
                    </button>
                    <Tooltip
                      id="chatProjectChip"
                      place="bottom"
                      className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                      {activeProject.name}
                    </Tooltip>
                    <span className="theme-text-subheading flex-shrink-0" aria-hidden="true">
                      /
                    </span>
                  </>
                )}
                <h4
                  className={`text-theme-heading text-heading-default inline-flex items-center transition-transform duration-300 ease-in-out cursor-default ${
                    isTitleHovered ? 'translate-x-[-10px]' : ''
                  }`}
                  data-testid="chatTitle"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  title={title?.length > (isDrawerMode || isRightPanelOpen ? 17 : 50) ? title : ''}
                  onMouseEnter={() => setIsTitleHovered(true)}
                  onMouseLeave={() => setIsTitleHovered(false)}>
                  {title?.length > (isDrawerMode || isRightPanelOpen ? 17 : 50)
                    ? `${title?.substring(0, isDrawerMode || isRightPanelOpen ? 17 : 50)}...`
                    : title}
                  {title !== null && (
                    <IconEdit
                      data-testid="editChatName"
                      className={`transition-all duration-300 ease-in-out cursor-pointer ${
                        isTitleHovered ? 'opacity-100 ml-[10px]' : 'opacity-0 ml-0'
                      }`}
                      color="var(--text-subheading)"
                      onClick={() => {
                        setTempTitle(title)
                        setIsEditName(true)
                      }}
                    />
                  )}
                </h4>
              </div>
            )}
          </div>
        )}
        {/* Right icons */}
        {(!isEditName || !isDrawerMode) && (
          <div
            className={`flex h-10 justify-end items-center bg-theme-surface ${
              !isDrawerMode ? 'justify-self-end' : 'ml-auto'
            } min-w-0`}>
            <UncontrolledDropdown>
              <DropdownToggle
                tag="div"
                id="help"
                className="ali-help inline-flex h-10 w-10 items-center justify-center hover:bg-theme-hover rounded-full transition-colors duration-200 cursor-pointer">
                <IconHelpCircle size={24} data-tooltip-id="help" className="focus:!outline-none" />
              </DropdownToggle>
              <Tooltip
                id="help"
                place="bottom"
                className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                Help
              </Tooltip>
              <DropdownMenu
                end
                className="w-fit bg-[var(--surface-muted)] border border-[var(--theme-border)] shadow-lg">
                <DropdownItem
                  data-testid="feedbackBtn"
                  className="text-theme-body hover:bg-theme-hover"
                  onClick={() => {
                    trackFeedbackClick({})
                    setIsFeedbackModalOpen(!isFeedbackModalOpen)
                  }}>
                  <span className="align-middle my-auto">Submit feedback</span>
                </DropdownItem>
                {!isDrawerMode && (
                  <DropdownItem
                    data-testid="tourBtn"
                    className="text-theme-body hover:bg-theme-hover"
                    onClick={() => setIsAliTourTriggered(true)}>
                    <span className="align-middle my-auto">Replay tour</span>
                  </DropdownItem>
                )}
                <DropdownItem
                  tag="a"
                  href={centraliGuides}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-theme-body hover:bg-theme-hover"
                  data-testid="userGuides">
                  <span className="align-middle my-auto">User guides</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            {!isDrawerMode && !centrali3_0 && (
              <>
                <img
                  data-tooltip-id="centraliHome"
                  src={CentraliIcon}
                  width="40px"
                  height="40px"
                  alt="logo"
                  className={`ml-1 h-[40px] cursor-pointer exit-to-centrali theme-image-hover chat-primary`}
                  onClick={() => navigate('/app-store')}
                />
                <Tooltip
                  id="centraliHome"
                  place="bottom"
                  className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                  Centrali Home
                </Tooltip>
              </>
            )}
            {/* <ThemeToggler /> */}
            {/*commenting out this part till custom persona release
             {!showSetting && (
              <>
                <Settings
                  id="settings"
                  className="cursor-pointer hover-effect ms-1"
                  onClick={() => setShowSetting(!showSetting)}
                />
                <UncontrolledTooltip placement="bottom" target="settings">
                  Settings
                </UncontrolledTooltip>
              </>
            )} */}
            {isDrawerMode && (
              <>
                {!isMarketplace && centrali3_0 ? (
                  isFullScreen ? (
                    <>
                      <div className="inline-flex h-10 w-10 items-center justify-center hover:bg-theme-hover rounded-full transition-colors duration-200 cursor-pointer">
                        <IconWindowMinimize
                          data-tooltip-id="minimizeAli"
                          size={24}
                          onClick={() => dispatch(setFullScreen(false))}
                          className="focus:!outline-none"
                        />
                      </div>
                      <Tooltip
                        id="minimizeAli"
                        place="bottom"
                        className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                        Minimize
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <div className="inline-flex h-10 w-10 items-center justify-center hover:bg-theme-hover rounded-full transition-colors duration-200 cursor-pointer">
                        <IconWindowMaximize
                          data-tooltip-id="maximizeAli"
                          size={24}
                          onClick={() => dispatch(setFullScreen(true))}
                          className="focus:!outline-none"
                        />
                      </div>
                      <Tooltip
                        id="maximizeAli"
                        place="bottom"
                        className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                        Maximize
                      </Tooltip>
                    </>
                  )
                ) : (
                  currentChat?.id && (
                    <>
                      <IconExternalLink
                        data-tooltip-id="fullScreen"
                        size={20}
                        className="ml-1 hover-theme-effect cursor-pointer"
                        onClick={() => {
                          dispatch(setAliOpenSideBar(false))
                          const chatPath = activeProject?.id
                            ? `/chat/project/${activeProject.id}/${currentChat?.id}`
                            : `/chat/${currentChat?.id}`
                          window.open(`${window.location.origin}${chatPath}`, '_blank')
                        }}
                      />
                      <Tooltip
                        id="fullScreen"
                        place="bottom"
                        className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                        Full Screen
                      </Tooltip>
                    </>
                  )
                )}
                <>
                  <div className="inline-flex h-10 w-10 items-center justify-center hover:bg-theme-hover rounded-full transition-colors duration-200 cursor-pointer">
                    <IconX
                      data-tooltip-id="closeAli"
                      size={24}
                      className="focus:!outline-none"
                      onClick={() => {
                        if (isMarketplace) {
                          dispatch(setIsChatOpen(false))
                          dispatch(setProducts([]))
                        } else {
                          dispatch(setAliOpenSideBar(false))
                        }
                      }}
                    />
                  </div>
                  <Tooltip
                    id="closeAli"
                    place="bottom"
                    className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                    Close
                  </Tooltip>
                </>
              </>
            )}
          </div>
        )}
      </div>
      {isFeedbackModalOpen && (
        <AliFeedbackModal
          isOpen={isFeedbackModalOpen}
          toggle={() => {
            setIsFeedbackModalOpen(!isFeedbackModalOpen)
          }}
        />
      )}
    </>
  )
}

export default ChatHeader
